import { useState, useEffect } from 'react'
import { supabase } from '@/integrations/supabase/client'
import { useToast } from '@/hooks/use-toast'

export interface MailboxProvider {
  id: string
  name: string
  display_name: string
  description?: string
  smtp_host?: string
  smtp_port?: number
  imap_host?: string
  imap_port?: number
  requires_app_password?: boolean
  setup_instructions?: string
  is_active: boolean
}

export function useMailboxProviders() {
  const [providers, setProviders] = useState<MailboxProvider[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const { toast } = useToast()

  const fetchProviders = async () => {
    try {
      const { data, error } = await supabase
        .from('mailbox_providers')
        .select('*')
        .eq('is_active', true)
        .order('display_name')

      if (error) throw error
      setProviders(data || [])
    } catch (error) {
      console.error('Error fetching mailbox providers:', error)
      toast({
        title: "Error",
        description: "Failed to load mailbox providers",
        variant: "destructive",
      })
    } finally {
      setIsLoading(false)
    }
  }

  const addProvider = async (provider: Omit<MailboxProvider, 'id'>) => {
    try {
      const { data, error } = await supabase
        .from('mailbox_providers')
        .insert([provider])
        .select()
        .single()

      if (error) throw error
      
      setProviders(prev => [...prev, data])
      toast({
        title: "Success",
        description: "Mailbox provider added successfully",
      })
      
      return data
    } catch (error) {
      console.error('Error adding mailbox provider:', error)
      toast({
        title: "Error",
        description: "Failed to add mailbox provider",
        variant: "destructive",
      })
      throw error
    }
  }

  useEffect(() => {
    fetchProviders()
  }, [])

  return {
    providers,
    isLoading,
    addProvider,
    refetch: fetchProviders
  }
}